import { HttpApiService } from "@/shared/services/httpApi.service";

import type {
  DepositAccountRow,
  DepositAccountsCorridor,
  DepositClabeFlavor,
  RegisterDepositAccountInput,
} from "../types/depositAccounts.types";

/**
 * Internal (staff) config of the deposit receiving accounts, one corridor per country. Backend contract
 * (coloca-nest `bitso-clabes.internal.controller.ts` / `bitso-cvus.internal.controller.ts` — exact mirrors,
 * BOTH endpoints of each are `userTypes: [INTERNAL]`):
 *
 * - `GET /internal/payments/bitso/{clabes|cvus}?organization_id=<uuid>` (`payments:read`) — `organization_id`
 *   is REQUIRED (ParseUUIDPipe → 400 otherwise). Returns a PLAIN ARRAY (not paginated), newest first
 *   (`created_at DESC`), non-deleted rows only. There is NO list-all endpoint.
 * - `POST /internal/payments/bitso/{clabes|cvus}` (`payments:write`) — registers a STATIC mapping (`is_active:
 *   true`). 201 always.
 *
 * IDEMPOTENCY GOTCHA (verified in `bitso-mx-clabe.service.ts` AND `bitso-ar-cvu.service.ts`): the register
 * looks the account up globally (`WHERE clabe/cvu AND deleted_at IS NULL` — NO org filter) and, if an active
 * mapping exists, returns THAT mapping with 201 — even when it belongs to ANOTHER organization. It never
 * errors on collision. Callers MUST compare `response.organization_id` against the requested org: a mismatch
 * means the account is already owned by another org and the request changed nothing.
 *
 * There is NO update / delete / deactivate / dynamic-mint endpoint — the UI must not imply those actions.
 */

/** Raw row of `GET/POST /internal/payments/bitso/clabes` (BitsoClabeAccountEntity). */
interface DepositClabeApiRow {
  id: string;
  clabe: string;
  organization_id: string;
  flavor: DepositClabeFlavor;
  provider_reference?: string | null;
  is_active: boolean;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

/** Raw row of `GET/POST /internal/payments/bitso/cvus` (BitsoCvuAccountEntity — no flavor/reference). */
interface DepositCvuApiRow {
  id: string;
  cvu: string;
  organization_id: string;
  is_active: boolean;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

function fromClabeRow(row: DepositClabeApiRow): DepositAccountRow {
  const { clabe, ...rest } = row;
  return { ...rest, account: clabe };
}

function fromCvuRow(row: DepositCvuApiRow): DepositAccountRow {
  const { cvu, ...rest } = row;
  return { ...rest, account: cvu };
}

/** Digits-only view of a (possibly grouped) account input, capped at the corridor's digit count. */
export function accountDigits(value: string, digitCount: number): string {
  return value.replace(/\D/g, "").slice(0, digitCount);
}

/** Backend rule (`RegisterBitsoClabeDto` / `RegisterBitsoCvuDto`): exactly N digits. */
export function isValidAccount(value: string, digitCount: number): boolean {
  return accountDigits(value, digitCount).length === digitCount;
}

/** Display grouping for legibility: `6461 8011 1122 2233 33`. Copy/submit always uses the raw digits. */
export function formatAccountGroups(value: string, digitCount: number): string {
  const digits = accountDigits(value, digitCount);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}

/** Grouped-input length: N digits + one space per full leading group of 4. */
export function groupedAccountMaxLength(digitCount: number): number {
  return digitCount + Math.floor((digitCount - 1) / 4);
}

const CLABES_URL = "/internal/payments/bitso/clabes";
const CVUS_URL = "/internal/payments/bitso/cvus";

/** Mexico SPEI receiving CLABEs (18 digits). */
export const DEPOSIT_CLABES_CORRIDOR: DepositAccountsCorridor = {
  id: "clabes",
  i18nNamespace: "depositClabes",
  digitCount: 18,
  async listByOrganization(organizationId: string): Promise<DepositAccountRow[]> {
    const query = new URLSearchParams({ organization_id: organizationId });
    const { data } = await HttpApiService.get<DepositClabeApiRow[]>(`${CLABES_URL}?${query}`);
    return data.map(fromClabeRow);
  },
  async register(input: RegisterDepositAccountInput): Promise<DepositAccountRow> {
    const { data } = await HttpApiService.post<
      DepositClabeApiRow,
      { organization_id: string; clabe: string; metadata?: Record<string, unknown> }
    >(CLABES_URL, {
      organization_id: input.organizationId,
      clabe: input.account,
      ...(input.metadata ? { metadata: input.metadata } : {}),
    });
    return fromClabeRow(data);
  },
};

/** Argentina receiving CVUs (22 digits). */
export const DEPOSIT_CVUS_CORRIDOR: DepositAccountsCorridor = {
  id: "cvus",
  i18nNamespace: "depositCvus",
  digitCount: 22,
  async listByOrganization(organizationId: string): Promise<DepositAccountRow[]> {
    const query = new URLSearchParams({ organization_id: organizationId });
    const { data } = await HttpApiService.get<DepositCvuApiRow[]>(`${CVUS_URL}?${query}`);
    return data.map(fromCvuRow);
  },
  async register(input: RegisterDepositAccountInput): Promise<DepositAccountRow> {
    const { data } = await HttpApiService.post<
      DepositCvuApiRow,
      { organization_id: string; cvu: string; metadata?: Record<string, unknown> }
    >(CVUS_URL, {
      organization_id: input.organizationId,
      cvu: input.account,
      ...(input.metadata ? { metadata: input.metadata } : {}),
    });
    return fromCvuRow(data);
  },
};
